import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { listMyOrdersDetailed } from "@/lib/orders.functions";
import { formatPrice, productSlug } from "@/lib/format";
import { optimizeImage, isDeadImageUrl } from "@/lib/image";
import { ChevronDown, PackageOpen } from "lucide-react";
import { ReturnDialog, type ReturnableItem } from "@/components/storefront/ReturnDialog";
import { OrderInvoices } from "@/components/storefront/OrderInvoices";
import { MyReturns } from "@/components/storefront/MyReturns";
import { RETURN_WINDOW_DAYS } from "@/lib/returns.functions";

export const Route = createFileRoute("/_authenticated/account/orders")({
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/account/orders" },{ title: "Οι παραγγελίες μου — MrPanda.gr" }] }),
  component: OrdersPage,
});

type OrderItem = {
  id: string;
  name: string;
  quantity: number;
  image_url: string | null;
  sku: string | null;
  unit_price: number | null;
};
type Order = {
  id: string;
  source: string | null;
  external_id: string | null;
  order_number: string | null;
  status: string;
  payment_status: string;
  payment_method: string;
  subtotal: number;
  shipping_cost: number;
  total: number;
  created_at: string;
  delivered_at: string | null;
  guest_access_token: string | null;
  raw?: { payment_provider?: string | null } | null;
  items: OrderItem[];
};

// Klarna orders are recorded as "viva" in the shared orders table (check
// constraint); the precise provider is kept in raw.payment_provider.
function displayPaymentMethod(o: Order): string {
  return o.raw?.payment_provider === "klarna" ? "klarna" : o.payment_method;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Σε εκκρεμότητα",
  processing: "Σε επεξεργασία",
  in_progress: "Σε επεξεργασία",
  shipped: "Απεστάλη",
  delivered: "Παραδόθηκε",
  cancelled: "Ακυρώθηκε",
  completed: "Ολοκληρώθηκε",
};
const PAY_LABELS: Record<string, string> = {
  cash: "Μετρητά",
  bank_transfer: "Τραπεζικό έμβασμα",
  iris: "IRIS",
  klarna: "Klarna",
  viva: "Κάρτα",
  viva_card: "Κάρτα",
};

// Imported (historical) orders not created from this eshop should always read
// as a finished state: "Ακυρώθηκε" if cancelled, otherwise "Παραδόθηκε".
function displayStatus(o: Order): string {
  const isEshop = o.source === "eshop" || o.source == null;
  if (!isEshop) {
    return o.status === "cancelled" ? "Ακυρώθηκε" : "Παραδόθηκε";
  }
  // If the order carries a delivery timestamp it has been delivered, even when
  // the status column was left at an earlier state (e.g. "shipped").
  if (o.status !== "cancelled" && o.delivered_at) return STATUS_LABELS.delivered;
  return STATUS_LABELS[o.status] ?? o.status;
}

// An order is eligible for a return when it is not cancelled and the return
// window (14 days from delivery, falling back to the order date) is still open.
function returnEligible(o: Order): boolean {
  if (o.status === "cancelled") return false;
  const base = new Date(o.delivered_at ?? o.created_at);
  const days = (Date.now() - base.getTime()) / 86_400_000;
  return Number.isFinite(days) && days <= RETURN_WINDOW_DAYS;
}

function OrdersPage() {
  const fn = useServerFn(listMyOrdersDetailed);
  const { data, isLoading } = useQuery({ queryKey: ["my-orders-detailed"], queryFn: () => fn() });
  const allOrders = (data?.orders ?? []) as Order[];
  // Hide online (Viva) orders that have not been paid — they only become real
  // orders once payment is confirmed.
  const orders = allOrders.filter((o) => {
    const online =
      o.payment_method === "viva" ||
      o.payment_method === "viva_card" ||
      o.payment_method === "klarna" ||
      o.payment_method === "iris";
    // Only hide brand-new eshop online orders that have not been paid yet.
    // Imported (historical) orders are always real and must always show.
    const isEshop = o.source === "eshop" || o.source == null;
    return !(isEshop && online && o.payment_status !== "paid");
  });
  const [open, setOpen] = useState<string | null>(null);
  const [returnOrder, setReturnOrder] = useState<Order | null>(null);

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold">📦 Οι παραγγελίες μου</h2>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Φόρτωση...</p>
      ) : orders.length === 0 ? (
        <div className="rounded-xl bg-muted/50 p-10 text-center">
          <div className="mb-2 text-4xl">🛒</div>
          <p className="text-sm text-muted-foreground">Δεν έχεις παραγγελίες ακόμη.</p>
          <Link to="/" className="mt-3 inline-block text-sm font-medium text-primary underline">Ξεκίνα τις αγορές</Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {orders.map((o) => {
            const isOpen = open === o.id;
            return (
              <li key={o.id} className="overflow-hidden rounded-xl border border-border">
                <button
                  onClick={() => setOpen(isOpen ? null : o.id)}
                  className="flex w-full items-center justify-between gap-3 bg-muted/30 px-4 py-3 text-left transition hover:bg-muted/60"
                >
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{o.order_number ?? o.external_id}</div>
                    <div className="font-semibold">{new Date(o.created_at).toLocaleDateString("el-GR")}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{displayStatus(o)}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{PAY_LABELS[displayPaymentMethod(o)] ?? displayPaymentMethod(o)}</span>
                      {(o.payment_method === "viva" || o.payment_method === "viva_card" || o.payment_method === "klarna" || o.payment_method === "iris") &&
                        o.payment_status !== "paid" && (
                          <span className="rounded-full bg-amber-400/15 px-2 py-0.5 font-medium text-amber-600 dark:text-amber-400">
                            {o.payment_status === "failed" ? "Πληρωμή απέτυχε" : "Αναμονή πληρωμής"}
                          </span>
                        )}
                      {o.payment_status === "paid" && (
                        <span className="rounded-full bg-success/15 px-2 py-0.5 font-medium text-success">Πληρώθηκε</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-right">
                    <div>
                      <div className="text-price font-bold">{formatPrice(Number(o.total))}</div>
                      <div className="text-xs text-muted-foreground">{o.items.length} προϊόντα</div>
                    </div>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-border p-4">
                    <ul className="space-y-3">
                      {o.items.map((it) => {
                        const inner = (
                          <>
                            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-muted">
                              {it.image_url && !isDeadImageUrl(it.image_url) ? (
                                <img src={optimizeImage(it.image_url, { width: 80 })} alt={it.name} width={80} height={80} loading="lazy" decoding="async" className="h-full w-full object-contain" />
                              ) : (
                                <div className="grid h-full w-full place-items-center text-xl">🐼</div>
                              )}
                            </div>
                            <div className="flex flex-1 items-center justify-between gap-2">
                              <span className="line-clamp-2 text-sm font-medium group-hover:text-primary">{it.name}</span>
                              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">×{it.quantity}</span>
                            </div>
                          </>
                        );
                        return (
                          <li key={it.id}>
                            {it.sku ? (
                              <Link
                                to="/p/$slug"
                                params={{ slug: productSlug(it.name, it.sku, undefined) }}
                                className="group flex items-center gap-3"
                              >
                                {inner}
                              </Link>
                            ) : (
                              <div className="flex items-center gap-3">{inner}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <dl className="mt-4 space-y-1 border-t border-border pt-3 text-sm">
                      <div className="flex justify-between"><dt className="text-muted-foreground">Υποσύνολο</dt><dd>{formatPrice(Number(o.subtotal))}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Μεταφορικά</dt><dd>{Number(o.shipping_cost) === 0 ? "Δωρεάν" : formatPrice(Number(o.shipping_cost))}</dd></div>
                      <div className="flex justify-between text-base font-bold"><dt>Σύνολο</dt><dd className="text-price">{formatPrice(Number(o.total))}</dd></div>
                    </dl>
                    <OrderInvoices orderId={o.id} />
                    {returnEligible(o) && (
                      <button
                        onClick={() => setReturnOrder(o)}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition hover:bg-primary/10"
                      >
                        <PackageOpen className="h-3.5 w-3.5" /> Επιστροφή προϊόντος
                      </button>
                    )}
                    {o.guest_access_token && (
                      <Link
                        to="/order/$token"
                        params={{ token: o.guest_access_token }}
                        search={{ view: true }}
                        className="mt-3 inline-block text-xs font-medium text-primary underline"
                      >
                        Πλήρεις λεπτομέρειες παραγγελίας
                      </Link>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {returnOrder && (
        <ReturnDialog
          open={!!returnOrder}
          onOpenChange={(v) => !v && setReturnOrder(null)}
          orderId={returnOrder.id}
          orderNumber={returnOrder.order_number ?? returnOrder.external_id}
          items={
            returnOrder.items.map((it) => ({
              id: it.id,
              name: it.name,
              quantity: it.quantity,
              unitPrice: (it as { unit_price?: number | null }).unit_price ?? null,
            })) as ReturnableItem[]
          }
        />
      )}
      <MyReturns />
    </div>
  );
}
